import {
  ArrowLeftIcon,
  Heading,
  IconButton,
  InfoIcon,
  InstagramGridIcon,
  ShopgridIcon,
  ToggleGroup,
  Tooltip,
} from '@buffer-mono/popcorn'
import type * as React from 'react'
import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom-v5-compat'

import { PageHeader } from '~publish/components/PageHeader'
import { PageLayout } from '~publish/components/PageLayout'
import { selectProfileById } from '~publish/legacy/profile-sidebar/selectors'
import { useAppSelector } from '~publish/legacy/store'
import Grid from '~publish/legacy/grid/components/GridPreview'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@apollo/client'

export enum Views {
  InstagramPreview = 'instagram-preview',
  ShopGrid = 'shop-grid',
}

export const BACKFILL_UPDATES_FOR_GRID = gql`
  mutation BackfillUpdatesForGrid($input: BackfillUpdatesForGridInput!) {
    backfillUpdatesForGrid(input: $input) {
      ... on MutationSuccess {
        _empty
      }
    }
  }
`

export const GET_CUSTOM_LINKS = gql`
  query ShopGridCustomLinks($input: ShopGridCustomLinksInput!) {
    shopGridCustomLinks(input: $input) {
      color
      contrastColor
      links {
        id
        text
        url
      }
    }
  }
`

export const ChannelShopGrid = (): JSX.Element => {
  const channelId =
    useParams<{
      id: string
    }>().id || ''
  const [backfillUpdatesForGrid] = useMutation(BACKFILL_UPDATES_FOR_GRID)

  const profile = useAppSelector((state) => selectProfileById(state, channelId))

  const { data } = useQuery(GET_CUSTOM_LINKS, {
    variables: {
      input: {
        channelId,
      },
    },
  })

  const [selectedView, setSelectedView] = useState(Views.InstagramPreview)

  useEffect(() => {
    if (channelId) {
      backfillUpdatesForGrid({ variables: { input: { channelId } } })
    }
  }, [backfillUpdatesForGrid, channelId])

  useEffect(() => {
    BufferTrackerReact.instagramGridPreviewPageLoaded({
      product: 'publish',
      clientName: 'publishWeb',
      channelId,
    })
  }, [channelId])

  return (
    <PageLayout>
      <PageLayout.Header separator={false}>
        <PageLayout.HeaderRow>
          <PageHeader.Title>
            <IconButton
              as={Link}
              to={`/channels/${channelId}`}
              label="Back to Channel"
              tooltip="Back to Channel"
              variant="tertiary"
            >
              <ArrowLeftIcon />
            </IconButton>
            <Heading>Grid</Heading>
            <Tooltip content="This is an approximation of your post grid. It may differ on other devices, and natively posted images update every 24 hours.">
              <InfoIcon />
            </Tooltip>
          </PageHeader.Title>
          <ToggleGroup
            value={selectedView}
            onChange={(value): void => {
              if (value) {
                setSelectedView(value as Views)

                BufferTrackerReact.instagramGridPreviewToggleView({
                  product: 'publish',
                  clientName: 'publishWeb',
                  channelId,
                  view: value,
                })
              }
            }}
            size="large"
          >
            <ToggleGroup.Item value={Views.InstagramPreview}>
              <InstagramGridIcon />
              Instagram Preview
            </ToggleGroup.Item>
            <ToggleGroup.Item value={Views.ShopGrid}>
              <ShopgridIcon />
              Shop Grid
            </ToggleGroup.Item>
          </ToggleGroup>
        </PageLayout.HeaderRow>
      </PageLayout.Header>
      <PageLayout.Container
        style={
          {
            '--page-layout-max-width': '932px',
            '--page-layout-padding': 0,
          } as React.CSSProperties
        }
      >
        {profile && data && (
          <Grid
            selectedView={selectedView}
            profile={profile}
            customLinksDetails={data.shopGridCustomLinks}
          />
        )}
      </PageLayout.Container>
    </PageLayout>
  )
}
